import * as React from "react";
import { useMachine } from "@xstate/react";
import {
  errorResponse,
  IAddressData,
  IVerifyAddressResponse,
  verifyAddress,
} from "@nju/data-access-portal-wwt";
import { IScope } from "@nju/scope";
import {
  clearFormError,
  model,
  setApartmentNumber,
  setApartmentNumberError,
  setCity,
  setHouseNumber,
  setStreet,
  setWithoutStreet,
  validate,
} from "./model";

const createWWTMachine = ({
  onPositiveVerification,
  onNegativeVerification,
  variant,
}: {
  onPositiveVerification: ({
    address,
  }: { address: IAddressData } & IVerifyAddressResponse) => void;
  onNegativeVerification: ({
    address,
  }: {
    address: IAddressData;
  } & { errorCode: string | number }) => void;
  variant: IScope;
}) => {
  return model.createMachine(
    {
      initial: "ready",
      states: {
        ready: {
          on: {
            setCity: {
              actions: setCity,
            },
            setStreet: {
              actions: setStreet,
            },
            setWithoutStreet: {
              actions: setWithoutStreet,
            },
            setHouseNumber: {
              actions: setHouseNumber,
            },
            setApartmentNumber: {
              actions: setApartmentNumber,
            },
            verify: {
              target: "validate",
            },
          },
        },
        validate: {
          entry: validate,
          always: [
            {
              target: "verify",
              cond: "hasNoErrors",
            },
            {
              target: "ready",
            },
          ],
        },
        verify: {
          entry: clearFormError,
          invoke: {
            src: "validateAddress",
          },
          on: {
            addressVerified: {
              actions: (
                { city, houseNumber, apartmentNumber, street },
                { installationToken },
              ) => {
                if (!city) {
                  return;
                }
                onPositiveVerification({
                  address: {
                    cityId: city.id,
                    cityName: city.name,
                    houseNumber,
                    apartmentNumber,
                    streetId: street?.id,
                    streetName: street?.name,
                  },
                  installationToken: installationToken,
                });
              },
            },
            addressNotVerified: {
              actions: (
                { city, houseNumber, apartmentNumber, street },
                { errorCode },
              ) => {
                if (!city) {
                  return;
                }
                onNegativeVerification({
                  address: {
                    cityId: city.id,
                    cityName: city.name,
                    houseNumber,
                    apartmentNumber,
                    streetId: street?.id,
                    streetName: street?.name,
                  },
                  errorCode,
                });
              },
            },
            setApartmentNumberError: {
              actions: setApartmentNumberError,
              target: "ready",
            },
          },
        },
      },
    },
    {
      services: {
        validateAddress: ({ city, street, houseNumber, apartmentNumber }) => {
          return async (callback) => {
            if (!city) {
              callback({ type: "missingCity" });
              return;
            }

            const result = await verifyAddress({
              cityId: city.id,
              cityName: city.name,
              streetId: street?.id,
              streetName: street?.name,
              houseNumber: houseNumber,
              apartmentNumber: apartmentNumber,
              variant,
            });

            if (result.isError) {
              if (result.err.code === "MISSING_APARTMENT_NUMBER") {
                return callback({ type: "setApartmentNumberError" });
              }

              const errorInfo = errorResponse.safeParse(result.err.extra?.json);
              const errorCode = errorInfo.success ? errorInfo.data.code : "";

              return callback({ type: "addressNotVerified", errorCode });
            }

            callback({
              type: "addressVerified",
              cityId: city.id,
              cityName: city.name,
              streetId: street?.id,
              streetName: street?.name,
              houseNumber: houseNumber,
              apartmentNumber: apartmentNumber,
              installationToken: result.value.installationToken,
            });
          };
        },
      },
      guards: {
        hasNoErrors: (context) => {
          return (
            context.cityError === undefined &&
            context.streetError === undefined &&
            context.houseNumberError === undefined &&
            context.apartmentNumberError === undefined
          );
        },
      },
    },
  );
};

export function useWWTMachine({
  onPositiveVerification,
  onNegativeVerification,
  variant,
}: {
  onPositiveVerification: ({
    address,
  }: { address: IAddressData } & IVerifyAddressResponse) => void;
  onNegativeVerification: ({
    address,
  }: {
    address: IAddressData;
  } & { errorCode: string | number }) => void;
  variant: IScope;
}) {
  return useMachine(
    React.useMemo(() => {
      return createWWTMachine({
        onPositiveVerification,
        onNegativeVerification,
        variant,
      });
    }, [onNegativeVerification, onPositiveVerification, variant]),
  );
}
